import React, { useMemo, useState } from "react";
import { Container, Row, Col, Card, CardHeader, Button } from "reactstrap";
import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useGetProductsQuery } from "slices/resourceInventory/inventorySlice";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import { ProductEdit } from "./ProductEdit";

const ProductListView = () => {
  const [editingProductId, setEditingProductId] = useState<string | undefined>(
    undefined,
  ); // State to manage add/edit mode
  const [isAdding, setIsAdding] = useState(false); // State to track if adding a new product

  // Fetch product data
  const {
    data: productData,
    isLoading: isLoadingProducts,
    refetch: refetchProducts,
  } = useGetProductsQuery(undefined);

  // Handle Add and Edit actions
  const handleAddProduct = () => {
    setEditingProductId(undefined); // Clear editing ID for new product
    setIsAdding(true); // Set to adding mode
  };

  const handleEditProduct = (id: string) => {
    setEditingProductId(id); // Set the ID for editing
    setIsAdding(false); // Set to editing mode
  };

  // Handle returning to list view
  const handleBackToList = () => {
    setEditingProductId(undefined); // Clear the state
    setIsAdding(false); // Exit adding/editing mode
  };

  // Define table columns for products
  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        filterable: false,
      },
      {
        Header: "Description",
        accessor: "description",
        filterable: false,
      },
      {
        Header: "Category",
        accessor: "category",
        filterable: false,
      },
      {
        Header: "Supplier",
        accessor: "supplier",
        filterable: false,
      },
      {
        Header: "Measurement",
        accessor: "measurement",
        filterable: false,
      },
      {
        Header: "Supplier Address",
        accessor: "supplier_address",
        filterable: false,
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }: any) => {
          const productId = row.original.id;
          return (
            <div>
              <ul className="list-inline hstack gap-2 mb-0">
                <li className="list-inline-item edit" title="Edit">
                  <Link
                    to="#"
                    className="text-primary d-inline-block edit-item-btn"
                    onClick={() => handleEditProduct(productId)}
                  >
                    <i className="ri-pencil-fill fs-16"></i>
                  </Link>
                </li>
                <li className="list-inline-item view" title="View">
                  <Link
                    to={`/product-view/${productId}`}
                    className="text-info d-inline-block view-item-btn"
                  >
                    <i className="ri-eye-fill fs-16"></i>
                  </Link>
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [],
  );

  document.title = "Products | Dashboard";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Products List" pageTitle="Products" />
          {(editingProductId || isAdding) && (
            <Row className="mt-4">
              <Col lg={12}>
                <ProductEdit
                  productId={editingProductId}
                  setCardView={handleBackToList} // Pass function to go back to list view
                />
              </Col>
            </Row>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Products List</h5>
                  <Button
                    color="success"
                    onClick={handleAddProduct}
                    disabled={isLoadingProducts}
                  >
                    <i className="ri-add-line align-bottom me-1"></i> Add
                    Product
                  </Button>
                </CardHeader>
                <div className="card-body pt-0">
                  {isLoadingProducts ? (
                    <Loader />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={productData?.results || []}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={10}
                      className="custom-header-css"
                      // isCustomerFilter={true}
                      SearchPlaceholder={"Search for products..."}
                      isPagination={true}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default ProductListView;
